import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { mockAuctions } from "@/mocks/auctions";
import { getLandValueByLatLng, calcLandPrice } from "@/mocks/landValues";

// ─── 収益計算ヘルパー ─────────────────────────────────────────
// 【面積の基準】
//   ゲーム内面積はシューレース公式 + Haversine補正で計算した実際のkm²
//   1km² = 100 coins（基本価格）× 地価係数
//
// 【収益計算式】
//   住宅: 居住者1人あたり houseIncome coins/h（地価係数込み）
//   発電所: 契約中MW × landMultiplier × 0.5 coins/h/MW（稼働中のみ）
//   会社: (従業員数 × 10 + 製品数 × 50) × landMultiplier coins/h
//
// ※土地を持つだけでは収益ゼロ。建造物を建て、運営して初めて稼げる

export function calcHouseIncome(
  residents: number,
  lat: number,
  lng: number
): number {
  if (residents === 0) return 0;
  const lv = getLandValueByLatLng(lat, lng);
  // 居住者1人あたり houseIncome coins/h（地価係数込み）
  return Math.round(residents * lv.houseIncome);
}

export function calcPowerPlantIncome(
  contractedMW: number,
  lat: number,
  lng: number,
  operatingStatus: string
): number {
  if (operatingStatus !== "running" || contractedMW === 0) return 0;
  const lv = getLandValueByLatLng(lat, lng);
  // 契約MW × 地価係数 × 0.5 coins/h/MW
  return Math.round(contractedMW * lv.landMultiplier * 0.5);
}

export function calcCompanyIncome(
  employeeCount: number,
  productCount: number,
  lat: number,
  lng: number
): number {
  if (employeeCount === 0 && productCount === 0) return 0;
  const lv = getLandValueByLatLng(lat, lng);
  // 従業員数 × 10 + 製品数 × 50、地価係数で乗算
  return Math.round((employeeCount * 10 + productCount * 50) * lv.landMultiplier);
}

export type BuildingType = "house" | "company" | "powerPlant";

// ─── 住宅データ ───────────────────────────────────────────────
export interface HouseData {
  id: string;
  blockId: string;
  ownerId: string;
  residents: Resident[];
  maxResidents: number;
  builtAt: string;
  condition: number;
}

export interface Resident {
  id: string;
  name: string;
  age: number;
  occupation: string;
  movedInAt: string;
  employerId?: string;
}

// ─── 株式データ ───────────────────────────────────────────────
export interface StockHolding {
  companyId: string;
  shares: number;
  avgBuyPrice: number;
  acquiredAt: string;
}

export interface StockOrder {
  id: string;
  companyId: string;
  type: "buy" | "sell";
  orderType: "market" | "limit";
  shares: number;
  limitPrice?: number;
  placedBy: string;
  placedByName: string;
  placedAt: string;
  status: "open" | "filled" | "cancelled";
  filledAt?: string;
  filledPrice?: number;
}

export interface StockTrade {
  id: string;
  companyId: string;
  buyerId: string;
  sellerId: string;
  shares: number;
  price: number;
  tradedAt: string;
}

export interface StockPricePoint {
  time: string;
  price: number;
  volume: number;
}

export interface StockMarketData {
  companyId: string;
  priceHistory: StockPricePoint[];
  orders: StockOrder[];
  trades: StockTrade[];
  // 株主リスト: userId -> shares
  shareholders: Record<string, { userId: string; userName: string; shares: number }>;
  dayHigh: number;
  dayLow: number;
  dayVolume: number;
  prevClose: number;
}

// ─── 会社データ ───────────────────────────────────────────────
export interface CompanyData {
  id: string;
  blockId: string;
  ownerId: string;
  name: string;
  industry: CompanyIndustry;
  description: string;
  foundedAt: string;
  employees: Employee[];
  maxEmployees: number;
  products: Product[];
  finances: CompanyFinances;
  stockPrice: number;
  totalShares: number;
  publicShares: number;
  isPublic: boolean;
  level: number;
  reputation: number;
}

export type CompanyIndustry =
  | "technology"
  | "manufacturing"
  | "finance"
  | "retail"
  | "energy"
  | "healthcare"
  | "food"
  | "construction"
  | "media"
  | "logistics";

export interface Employee {
  id: string;
  name: string;
  role: EmployeeRole;
  salary: number;
  hiredAt: string;
  performance: number;
  department: string;
  skills: string[];
}

export type EmployeeRole =
  | "ceo"
  | "cto"
  | "cfo"
  | "manager"
  | "engineer"
  | "designer"
  | "sales"
  | "hr"
  | "accountant"
  | "worker";

export interface Product {
  id: string;
  name: string;
  category: string;
  productionCost: number;
  sellingPrice: number;
  monthlyProduction: number;
  stockCount: number;
  launchedAt: string;
  status: "active" | "discontinued" | "development";
  description: string;
}

export interface CompanyFinances {
  revenue: number;
  expenses: number;
  profit: number;
  totalAssets: number;
  totalLiabilities: number;
  cashBalance: number;
  monthlyHistory: MonthlyFinance[];
}

export interface MonthlyFinance {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

// ─── 発電所データ ─────────────────────────────────────────────
export interface PowerPlantData {
  id: string;
  blockId: string;
  ownerId: string;
  name: string;
  type: PowerPlantType;
  capacity: number;
  currentOutput: number;
  efficiency: number;
  fuelLevel: number;
  builtAt: string;
  lastMaintenance: string;
  maintenanceCost: number;
  powerContracts: PowerContract[];
  totalGenerated: number;
  co2Emission: number;
  operatingStatus: "running" | "maintenance" | "offline";
}

export type PowerPlantType =
  | "coal"
  | "gas"
  | "nuclear"
  | "solar"
  | "wind"
  | "hydro"
  | "geothermal";

export interface PowerContract {
  id: string;
  clientId: string;
  clientName: string;
  contractedMW: number;
  pricePerMWh: number;
  startDate: string;
  endDate: string;
  status: "active" | "expired" | "pending";
}

// ─── 建物共通 ─────────────────────────────────────────────────
export interface Building {
  type: BuildingType;
  builtAt: string;
  buildingId: string;
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface BlockData {
  blockId: string;
  lat: number;
  lng: number;
  polygon: LatLng[];      // ポリゴン頂点リスト
  areaKm2: number;        // 面積（km²）
  ownerId: string | null;
  ownerName: string | null;
  buildings: Building[];
  isForSale: boolean;
  auctionEndTime: string | null;
  auctionDuration: "8h" | "24h" | "3d" | null;
  price: number;
}

export interface AuctionItem {
  id: string;
  blockId: string;
  blockLat: number;
  blockLng: number;
  sellerId: string;
  sellerName: string;
  startPrice: number;
  currentBid: number;
  currentBidder: string | null;
  endTime: string;
  duration: "8h" | "24h" | "3d";
}

// ─── コスト比率 ───────────────────────────────────────────────
export const BUILDING_COSTS: Record<BuildingType, number> = {
  house: 500,
  company: 10000,
  powerPlant: 50000,
};

export const BUILDING_COIN_INCOME: Record<BuildingType, number> = {
  house: 10,
  company: 200,
  powerPlant: 1000,
};

export const BLOCK_PRICE = 100;

// ─── ポリゴン重なり判定（Separating Axis Theorem 簡易版）────────
function polygonsOverlap(a: { lat: number; lng: number }[], b: { lat: number; lng: number }[]): boolean {
  // 点がポリゴン内にあるか（Ray casting）
  const pointInPoly = (pt: { lat: number; lng: number }, poly: { lat: number; lng: number }[]): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].lng, yi = poly[i].lat;
      const xj = poly[j].lng, yj = poly[j].lat;
      const intersect = yi > pt.lat !== yj > pt.lat && pt.lng < ((xj - xi) * (pt.lat - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };
  // どちらかの頂点が相手ポリゴン内にあれば重なり
  for (const pt of a) { if (pointInPoly(pt, b)) return true; }
  for (const pt of b) { if (pointInPoly(pt, a)) return true; }
  return false;
}

// ─── 発電所タイプ別スペック ───────────────────────────────────
export const POWER_PLANT_SPECS: Record<PowerPlantType, {
  label: string;
  capacity: number;
  efficiency: number;
  co2: number;
  buildCost: number;
  maintenanceCost: number;
  icon: string;
  color: string;
}> = {
  coal:        { label: "石炭火力",   capacity: 600,  efficiency: 38, co2: 0.82, buildCost: 50000, maintenanceCost: 2000, icon: "ri-fire-line",        color: "text-gray-400" },
  gas:         { label: "ガス火力",   capacity: 400,  efficiency: 55, co2: 0.49, buildCost: 40000, maintenanceCost: 1500, icon: "ri-gas-station-line", color: "text-blue-400" },
  nuclear:     { label: "原子力",     capacity: 1000, efficiency: 33, co2: 0.012,buildCost: 200000,maintenanceCost: 8000, icon: "ri-flashlight-fill",   color: "text-yellow-400" },
  solar:       { label: "太陽光",     capacity: 50,   efficiency: 20, co2: 0.041,buildCost: 30000, maintenanceCost: 500,  icon: "ri-sun-line",         color: "text-amber-400" },
  wind:        { label: "風力",       capacity: 100,  efficiency: 35, co2: 0.011,buildCost: 35000, maintenanceCost: 700,  icon: "ri-windy-line",       color: "text-cyan-400" },
  hydro:       { label: "水力",       capacity: 300,  efficiency: 90, co2: 0.024,buildCost: 80000, maintenanceCost: 1200, icon: "ri-water-flash-line", color: "text-blue-300" },
  geothermal:  { label: "地熱",       capacity: 80,   efficiency: 85, co2: 0.038,buildCost: 60000, maintenanceCost: 900,  icon: "ri-temp-hot-line",    color: "text-red-400" },
};

// ─── 業種別スペック ───────────────────────────────────────────
export const INDUSTRY_SPECS: Record<CompanyIndustry, {
  label: string;
  icon: string;
  color: string;
  baseRevenue: number;
  baseExpenses: number;
  maxEmployees: number;
}> = {
  technology:    { label: "テクノロジー", icon: "ri-computer-line",      color: "text-cyan-400",    baseRevenue: 5000,  baseExpenses: 3000, maxEmployees: 200 },
  manufacturing: { label: "製造業",       icon: "ri-tools-line",          color: "text-orange-400",  baseRevenue: 8000,  baseExpenses: 6000, maxEmployees: 500 },
  finance:       { label: "金融",         icon: "ri-bank-line",           color: "text-green-400",   baseRevenue: 10000, baseExpenses: 4000, maxEmployees: 150 },
  retail:        { label: "小売",         icon: "ri-store-2-line",        color: "text-pink-400",    baseRevenue: 3000,  baseExpenses: 2500, maxEmployees: 100 },
  energy:        { label: "エネルギー",   icon: "ri-flashlight-line",     color: "text-yellow-400",  baseRevenue: 12000, baseExpenses: 8000, maxEmployees: 300 },
  healthcare:    { label: "医療",         icon: "ri-hospital-line",       color: "text-red-400",     baseRevenue: 7000,  baseExpenses: 5000, maxEmployees: 250 },
  food:          { label: "食品",         icon: "ri-restaurant-line",     color: "text-amber-400",   baseRevenue: 4000,  baseExpenses: 3500, maxEmployees: 200 },
  construction:  { label: "建設",         icon: "ri-building-2-line",     color: "text-stone-400",   baseRevenue: 6000,  baseExpenses: 5000, maxEmployees: 400 },
  media:         { label: "メディア",     icon: "ri-tv-2-line",           color: "text-purple-400",  baseRevenue: 4500,  baseExpenses: 2500, maxEmployees: 100 },
  logistics:     { label: "物流",         icon: "ri-truck-line",          color: "text-indigo-400",  baseRevenue: 5500,  baseExpenses: 4500, maxEmployees: 350 },
};

export const EMPLOYEE_ROLES: Record<EmployeeRole, { label: string; baseSalary: number; department: string }> = {
  ceo:        { label: "CEO",         baseSalary: 5000,  department: "経営" },
  cto:        { label: "CTO",         baseSalary: 4000,  department: "技術" },
  cfo:        { label: "CFO",         baseSalary: 4000,  department: "財務" },
  manager:    { label: "マネージャー", baseSalary: 2000,  department: "管理" },
  engineer:   { label: "エンジニア",  baseSalary: 1800,  department: "技術" },
  designer:   { label: "デザイナー",  baseSalary: 1500,  department: "クリエイティブ" },
  sales:      { label: "営業",        baseSalary: 1200,  department: "営業" },
  hr:         { label: "人事",        baseSalary: 1300,  department: "人事" },
  accountant: { label: "経理",        baseSalary: 1400,  department: "財務" },
  worker:     { label: "作業員",      baseSalary: 800,   department: "製造" },
};

// ─── 株式市場モックデータ生成 ─────────────────────────────────
function generatePriceHistory(basePrice: number, days: number): StockPricePoint[] {
  const points: StockPricePoint[] = [];
  let price = basePrice;
  const now = Date.now();
  for (let i = days * 24; i >= 0; i--) {
    const change = (Math.random() - 0.48) * price * 0.02;
    price = Math.max(1, price + change);
    points.push({
      time: new Date(now - i * 3600000).toISOString(),
      price: Math.round(price * 100) / 100,
      volume: Math.floor(Math.random() * 5000) + 500,
    });
  }
  return points;
}

const mockStockMarket: Record<string, StockMarketData> = {
  "company-001": {
    companyId: "company-001",
    priceHistory: generatePriceHistory(1250, 30),
    orders: [
      { id: "order-001", companyId: "company-001", type: "buy",  orderType: "limit", shares: 500,  limitPrice: 1240, placedBy: "user-002", placedByName: "Yamamoto_K",  placedAt: new Date(Date.now() - 3600000).toISOString(), status: "open" },
      { id: "order-002", companyId: "company-001", type: "buy",  orderType: "limit", shares: 1000, limitPrice: 1230, placedBy: "user-003", placedByName: "James_Smith", placedAt: new Date(Date.now() - 7200000).toISOString(), status: "open" },
      { id: "order-003", companyId: "company-001", type: "buy",  orderType: "limit", shares: 200,  limitPrice: 1220, placedBy: "user-004", placedByName: "Li_Wei",      placedAt: new Date(Date.now() - 10800000).toISOString(), status: "open" },
      { id: "order-004", companyId: "company-001", type: "sell", orderType: "limit", shares: 300,  limitPrice: 1260, placedBy: "user-005", placedByName: "Park_Joon",   placedAt: new Date(Date.now() - 1800000).toISOString(), status: "open" },
      { id: "order-005", companyId: "company-001", type: "sell", orderType: "limit", shares: 800,  limitPrice: 1270, placedBy: "user-006", placedByName: "Mueller_H",   placedAt: new Date(Date.now() - 5400000).toISOString(), status: "open" },
      { id: "order-006", companyId: "company-001", type: "sell", orderType: "limit", shares: 1500, limitPrice: 1280, placedBy: "user-007", placedByName: "Tanaka_M",    placedAt: new Date(Date.now() - 9000000).toISOString(), status: "open" },
    ],
    trades: [
      { id: "trade-001", companyId: "company-001", buyerId: "user-002", sellerId: "user-001", shares: 200, price: 1248, tradedAt: new Date(Date.now() - 1800000).toISOString() },
      { id: "trade-002", companyId: "company-001", buyerId: "user-003", sellerId: "user-005", shares: 500, price: 1252, tradedAt: new Date(Date.now() - 3600000).toISOString() },
      { id: "trade-003", companyId: "company-001", buyerId: "user-004", sellerId: "user-006", shares: 100, price: 1245, tradedAt: new Date(Date.now() - 7200000).toISOString() },
      { id: "trade-004", companyId: "company-001", buyerId: "user-007", sellerId: "user-002", shares: 300, price: 1255, tradedAt: new Date(Date.now() - 10800000).toISOString() },
      { id: "trade-005", companyId: "company-001", buyerId: "user-001", sellerId: "user-003", shares: 150, price: 1260, tradedAt: new Date(Date.now() - 14400000).toISOString() },
    ],
    shareholders: {
      "user-001": { userId: "user-001", userName: "あなた",       shares: 700000 },
      "user-002": { userId: "user-002", userName: "Yamamoto_K",  shares: 120000 },
      "user-003": { userId: "user-003", userName: "James_Smith", shares: 85000  },
      "user-004": { userId: "user-004", userName: "Li_Wei",      shares: 60000  },
      "user-005": { userId: "user-005", userName: "Park_Joon",   shares: 35000  },
    },
    dayHigh: 1268,
    dayLow: 1238,
    dayVolume: 12500,
    prevClose: 1235,
  },
  "company-002": {
    companyId: "company-002",
    priceHistory: generatePriceHistory(3800, 30),
    orders: [
      { id: "order-010", companyId: "company-002", type: "buy",  orderType: "limit", shares: 200, limitPrice: 3780, placedBy: "user-001", placedByName: "あなた",      placedAt: new Date(Date.now() - 2700000).toISOString(), status: "open" },
      { id: "order-011", companyId: "company-002", type: "sell", orderType: "limit", shares: 150, limitPrice: 3820, placedBy: "user-003", placedByName: "James_Smith", placedAt: new Date(Date.now() - 5400000).toISOString(), status: "open" },
    ],
    trades: [
      { id: "trade-010", companyId: "company-002", buyerId: "user-002", sellerId: "user-004", shares: 50,  price: 3795, tradedAt: new Date(Date.now() - 3600000).toISOString() },
      { id: "trade-011", companyId: "company-002", buyerId: "user-005", sellerId: "user-001", shares: 100, price: 3810, tradedAt: new Date(Date.now() - 7200000).toISOString() },
    ],
    shareholders: {
      "user-008": { userId: "user-008", userName: "Osaka_Finance", shares: 600000 },
      "user-002": { userId: "user-002", userName: "Yamamoto_K",    shares: 200000 },
      "user-001": { userId: "user-001", userName: "あなた",         shares: 50000  },
    },
    dayHigh: 3825,
    dayLow: 3762,
    dayVolume: 4800,
    prevClose: 3750,
  },
  "company-003": {
    companyId: "company-003",
    priceHistory: generatePriceHistory(520, 30),
    orders: [
      { id: "order-020", companyId: "company-003", type: "buy",  orderType: "limit", shares: 1000, limitPrice: 515, placedBy: "user-004", placedByName: "Li_Wei",    placedAt: new Date(Date.now() - 1800000).toISOString(), status: "open" },
      { id: "order-021", companyId: "company-003", type: "sell", orderType: "limit", shares: 500,  limitPrice: 525, placedBy: "user-005", placedByName: "Park_Joon", placedAt: new Date(Date.now() - 3600000).toISOString(), status: "open" },
    ],
    trades: [
      { id: "trade-020", companyId: "company-003", buyerId: "user-001", sellerId: "user-006", shares: 200, price: 518, tradedAt: new Date(Date.now() - 5400000).toISOString() },
    ],
    shareholders: {
      "user-009": { userId: "user-009", userName: "Global_Energy", shares: 500000 },
      "user-003": { userId: "user-003", userName: "James_Smith",   shares: 150000 },
    },
    dayHigh: 528,
    dayLow: 510,
    dayVolume: 8200,
    prevClose: 515,
  },
};

interface GameContextType {
  blocks: Record<string, BlockData>;
  landsLoading: boolean;
  auctions: AuctionItem[];
  companies: Record<string, CompanyData>;
  powerPlants: Record<string, PowerPlantData>;
  houses: Record<string, HouseData>;
  stockMarket: Record<string, StockMarketData>;
  purchaseBlock: (blockId: string, lat: number, lng: number, polygon: LatLng[], areaKm2: number, userId: string, userName: string, spendCoins: (n: number) => Promise<boolean>) => Promise<{ success: boolean; error?: string }>;
  buildOnBlock: (blockId: string, buildingType: BuildingType, userId: string, extraData?: Partial<CompanyData> | Partial<PowerPlantData>) => { success: boolean; buildingId?: string };
  listForAuction: (blockId: string, duration: "8h" | "24h" | "3d", userId: string) => boolean;
  placeBid: (auctionId: string, bidAmount: number, bidderId: string, bidderName: string) => boolean;
  getBlock: (blockId: string) => BlockData | undefined;
  // 会社操作
  hireEmployee: (companyId: string, employee: Omit<Employee, "id" | "hiredAt">) => boolean;
  fireEmployee: (companyId: string, employeeId: string) => boolean;
  addProduct: (companyId: string, product: Omit<Product, "id" | "launchedAt" | "stockCount">) => boolean;
  updateProduct: (companyId: string, productId: string, updates: Partial<Product>) => void;
  updateCompany: (companyId: string, updates: Partial<CompanyData>) => void;
  // 発電所操作
  addPowerContract: (plantId: string, contract: Omit<PowerContract, "id">) => boolean;
  updatePowerPlant: (plantId: string, updates: Partial<PowerPlantData>) => void;
  performMaintenance: (plantId: string, spendCoins: (n: number) => boolean) => boolean;
  // 株式市場操作
  goPublic: (companyId: string, publicShares: number, ipoPrice: number, spendCoins: (n: number) => boolean) => boolean;
  placeStockOrder: (order: Omit<StockOrder, "id" | "placedAt" | "status">, spendCoins: (n: number) => boolean, addCoins: (n: number) => void, holdings: Record<string, StockHolding>) => { success: boolean; message: string };
  cancelStockOrder: (companyId: string, orderId: string, addCoins: (n: number) => void) => boolean;
  getStockMarket: (companyId: string) => StockMarketData | undefined;
  updateStockHoldings: (companyId: string, userId: string, userName: string, sharesDelta: number) => void;
}

const GameContext = createContext<GameContextType | null>(null);

export const useGame = () => {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used within GameProvider");
  return ctx;
};

// ─── モック会社データ ─────────────────────────────────────────
const mockCompanies: Record<string, CompanyData> = {
  "company-001": {
    id: "company-001",
    blockId: "35.6812_139.7671_16",
    ownerId: "user-001",
    name: "Tokyo Tech Solutions",
    industry: "technology",
    description: "最先端のAI・クラウドソリューションを提供するテクノロジー企業",
    foundedAt: "2026-02-01T14:00:00Z",
    employees: [
      { id: "emp-001", name: "田中 一郎", role: "ceo", salary: 5000, hiredAt: "2026-02-01T14:00:00Z", performance: 92, department: "経営", skills: ["戦略立案", "リーダーシップ"] },
      { id: "emp-002", name: "鈴木 花子", role: "cto", salary: 4000, hiredAt: "2026-02-01T14:00:00Z", performance: 88, department: "技術", skills: ["AI", "クラウド", "Python"] },
      { id: "emp-003", name: "佐藤 次郎", role: "engineer", salary: 1800, hiredAt: "2026-02-15T09:00:00Z", performance: 85, department: "技術", skills: ["React", "TypeScript"] },
      { id: "emp-004", name: "山田 美咲", role: "sales", salary: 1200, hiredAt: "2026-03-01T10:00:00Z", performance: 78, department: "営業", skills: ["営業", "交渉"] },
    ],
    maxEmployees: 200,
    products: [
      { id: "prod-001", name: "CloudAI Platform", category: "SaaS", productionCost: 500, sellingPrice: 2000, monthlyProduction: 50, stockCount: 120, launchedAt: "2026-02-20T00:00:00Z", status: "active", description: "企業向けAIクラウドプラットフォーム" },
      { id: "prod-002", name: "DataSync Pro", category: "Software", productionCost: 200, sellingPrice: 800, monthlyProduction: 100, stockCount: 250, launchedAt: "2026-03-10T00:00:00Z", status: "active", description: "リアルタイムデータ同期ツール" },
    ],
    finances: {
      revenue: 180000,
      expenses: 52000,
      profit: 128000,
      totalAssets: 500000,
      totalLiabilities: 120000,
      cashBalance: 380000,
      monthlyHistory: [
        { month: "2026-01", revenue: 120000, expenses: 45000, profit: 75000 },
        { month: "2026-02", revenue: 150000, expenses: 48000, profit: 102000 },
        { month: "2026-03", revenue: 180000, expenses: 52000, profit: 128000 },
      ],
    },
    stockPrice: 1250,
    totalShares: 1000000,
    publicShares: 300000,
    isPublic: true,
    level: 3,
    reputation: 82,
  },
  "company-002": {
    id: "company-002",
    blockId: "51.5074_-0.1278_16",
    ownerId: "user-008",
    name: "Osaka Finance Group",
    industry: "finance",
    description: "グローバル金融サービスを提供する大手金融グループ",
    foundedAt: "2025-11-15T09:00:00Z",
    employees: [
      { id: "emp-010", name: "大阪 健太", role: "ceo", salary: 5000, hiredAt: "2025-11-15T09:00:00Z", performance: 95, department: "経営", skills: ["金融工学", "M&A"] },
      { id: "emp-011", name: "中村 理恵", role: "cfo", salary: 4000, hiredAt: "2025-11-15T09:00:00Z", performance: 91, department: "財務", skills: ["会計", "リスク管理"] },
    ],
    maxEmployees: 150,
    products: [
      { id: "prod-010", name: "投資信託 Premium", category: "金融商品", productionCost: 0, sellingPrice: 5000, monthlyProduction: 200, stockCount: 500, launchedAt: "2025-12-01T00:00:00Z", status: "active", description: "高利回り投資信託" },
    ],
    finances: {
      revenue: 450000,
      expenses: 180000,
      profit: 270000,
      totalAssets: 2000000,
      totalLiabilities: 800000,
      cashBalance: 1200000,
      monthlyHistory: [
        { month: "2026-01", revenue: 380000, expenses: 160000, profit: 220000 },
        { month: "2026-02", revenue: 420000, expenses: 170000, profit: 250000 },
        { month: "2026-03", revenue: 450000, expenses: 180000, profit: 270000 },
      ],
    },
    stockPrice: 3800,
    totalShares: 1000000,
    publicShares: 400000,
    isPublic: true,
    level: 4,
    reputation: 90,
  },
  "company-003": {
    id: "company-003",
    blockId: "40.7128_-74.0060_16",
    ownerId: "user-009",
    name: "Global Energy Corp",
    industry: "energy",
    description: "再生可能エネルギーに特化したグローバルエネルギー企業",
    foundedAt: "2025-09-01T00:00:00Z",
    employees: [
      { id: "emp-020", name: "Green_CEO", role: "ceo", salary: 5000, hiredAt: "2025-09-01T00:00:00Z", performance: 88, department: "経営", skills: ["再生可能エネルギー", "ESG"] },
    ],
    maxEmployees: 300,
    products: [
      { id: "prod-020", name: "太陽光パネル Pro", category: "エネルギー機器", productionCost: 800, sellingPrice: 2500, monthlyProduction: 300, stockCount: 800, launchedAt: "2025-10-01T00:00:00Z", status: "active", description: "高効率太陽光パネル" },
    ],
    finances: {
      revenue: 280000,
      expenses: 150000,
      profit: 130000,
      totalAssets: 1200000,
      totalLiabilities: 400000,
      cashBalance: 800000,
      monthlyHistory: [
        { month: "2026-01", revenue: 240000, expenses: 130000, profit: 110000 },
        { month: "2026-02", revenue: 260000, expenses: 140000, profit: 120000 },
        { month: "2026-03", revenue: 280000, expenses: 150000, profit: 130000 },
      ],
    },
    stockPrice: 520,
    totalShares: 2000000,
    publicShares: 800000,
    isPublic: true,
    level: 3,
    reputation: 85,
  },
};

// ─── モック発電所データ ───────────────────────────────────────
const mockPowerPlants: Record<string, PowerPlantData> = {
  "plant-001": {
    id: "plant-001",
    blockId: "48.8566_2.3522_16",
    ownerId: "user-002",
    name: "Paris Solar Farm",
    type: "solar",
    capacity: 50,
    currentOutput: 32,
    efficiency: 64,
    fuelLevel: 100,
    builtAt: "2026-01-20T08:00:00Z",
    lastMaintenance: "2026-03-15T00:00:00Z",
    maintenanceCost: 500,
    powerContracts: [
      { id: "contract-001", clientId: "user-003", clientName: "James_Smith", contractedMW: 15, pricePerMWh: 120, startDate: "2026-02-01", endDate: "2027-02-01", status: "active" },
    ],
    totalGenerated: 23040,
    co2Emission: 0.041,
    operatingStatus: "running",
  },
};

// ─── モック住宅データ ─────────────────────────────────────────
const mockHouses: Record<string, HouseData> = {
  "house-001": {
    id: "house-001",
    blockId: "35.6812_139.7671_16",
    ownerId: "user-001",
    residents: [
      { id: "res-001", name: "田中 太郎", age: 35, occupation: "エンジニア", movedInAt: "2026-01-20T00:00:00Z", employerId: "company-001" },
      { id: "res-002", name: "田中 花子", age: 32, occupation: "デザイナー", movedInAt: "2026-01-20T00:00:00Z" },
    ],
    maxResidents: 4,
    builtAt: "2026-01-15T10:00:00Z",
    condition: 95,
  },
};

export function GameProvider({ children }: { children: ReactNode }) {
  const [blocks, setBlocks] = useState<Record<string, BlockData>>({});
  const [landsLoading, setLandsLoading] = useState(true);
  const [auctions, setAuctions] = useState<AuctionItem[]>(mockAuctions);
  const [companies, setCompanies] = useState<Record<string, CompanyData>>(mockCompanies);
  const [powerPlants, setPowerPlants] = useState<Record<string, PowerPlantData>>(mockPowerPlants);
  const [houses, setHouses] = useState<Record<string, HouseData>>(mockHouses);
  const [stockMarket, setStockMarket] = useState<Record<string, StockMarketData>>(mockStockMarket);

  // ─── Supabaseから土地データを読み込む ─────────────────────────
  useEffect(() => {
    const fetchLands = async () => {
      setLandsLoading(true);
      const { data, error } = await supabase
        .from("lands")
        .select("*");

      if (!error && data) {
        const loaded: Record<string, BlockData> = {};
        for (const row of data) {
          loaded[row.id] = {
            blockId: row.id,
            lat: row.lat,
            lng: row.lng,
            polygon: row.polygon as LatLng[],
            areaKm2: row.area_km2,
            ownerId: row.owner_id,
            ownerName: row.owner_name,
            buildings: row.buildings as Building[],
            isForSale: row.is_for_sale,
            auctionEndTime: row.auction_end_time,
            auctionDuration: row.auction_duration as BlockData["auctionDuration"],
            price: row.price,
          };
        }
        setBlocks(loaded);
      }
      setLandsLoading(false);
    };

    fetchLands();

    // リアルタイム更新（他プレイヤーの購入を即反映）
    const channel = supabase
      .channel("lands_realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "lands" },
        (payload) => {
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            const row = payload.new as Record<string, unknown>;
            setBlocks((prev) => ({
              ...prev,
              [row.id as string]: {
                blockId: row.id as string,
                lat: row.lat as number,
                lng: row.lng as number,
                polygon: row.polygon as LatLng[],
                areaKm2: row.area_km2 as number,
                ownerId: row.owner_id as string,
                ownerName: row.owner_name as string,
                buildings: row.buildings as Building[],
                isForSale: row.is_for_sale as boolean,
                auctionEndTime: row.auction_end_time as string | null,
                auctionDuration: row.auction_duration as BlockData["auctionDuration"],
                price: row.price as number,
              },
            }));
          } else if (payload.eventType === "DELETE") {
            const oldRow = payload.old as Record<string, unknown>;
            setBlocks((prev) => {
              const next = { ...prev };
              delete next[oldRow.id as string];
              return next;
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getBlock = (blockId: string) => blocks[blockId];
  const getStockMarket = (companyId: string) => stockMarket[companyId];

  const purchaseBlock = async (
    blockId: string,
    lat: number,
    lng: number,
    polygon: LatLng[],
    areaKm2: number,
    userId: string,
    userName: string,
    spendCoins: (n: number) => Promise<boolean>
  ): Promise<{ success: boolean; error?: string }> => {
    // DBから最新の所有状況を確認（競合防止）
    const { data: existingLand } = await supabase
      .from("lands")
      .select("id, owner_id, owner_name")
      .eq("id", blockId)
      .maybeSingle();

    if (existingLand?.owner_id) {
      return { success: false, error: `この区画は既に ${existingLand.owner_name} が所有しています` };
    }

    const MIN_AREA_KM2 = 0.0001;
    const MAX_AREA_KM2 = 1;
    if (areaKm2 < MIN_AREA_KM2) {
      return { success: false, error: `面積が小さすぎます（${(areaKm2 * 1000000).toFixed(0)} m²）。100m²以上必要です` };
    }
    if (areaKm2 >= MAX_AREA_KM2) {
      return { success: false, error: `面積が大きすぎます（${areaKm2.toFixed(4)} km²）。1 km²未満にしてください` };
    }

    // 既存ポリゴンとの重なりチェック（ローカルキャッシュ）
    for (const existing of Object.values(blocks)) {
      if (!existing.ownerId || !existing.polygon?.length) continue;
      if (polygonsOverlap(polygon, existing.polygon)) {
        return { success: false, error: `${existing.ownerName} の土地と重なっています` };
      }
    }

    const totalPrice = calcLandPrice(areaKm2, lat, lng);
    const paid = await spendCoins(totalPrice);
    if (!paid) return { success: false, error: `コインが不足しています（必要: ${totalPrice} coins）` };

    // Supabaseに保存
    const { error: insertError } = await supabase.from("lands").insert({
      id: blockId,
      owner_id: userId,
      owner_name: userName,
      lat,
      lng,
      polygon,
      area_km2: areaKm2,
      buildings: [],
      is_for_sale: false,
      auction_end_time: null,
      auction_duration: null,
      price: totalPrice,
    });

    if (insertError) {
      // INSERT失敗 = 競合（他の人が先に購入）→ コイン返金
      // spendCoinsの逆操作はAuthContextのaddCoinsで行う必要があるが、
      // ここではエラーを返してUIで対処
      return { success: false, error: "購入に失敗しました。他のプレイヤーが先に購入した可能性があります" };
    }

    // ローカルステートも更新
    setBlocks((prev) => ({
      ...prev,
      [blockId]: {
        blockId, lat, lng,
        polygon,
        areaKm2,
        ownerId: userId,
        ownerName: userName,
        buildings: [],
        isForSale: false,
        auctionEndTime: null,
        auctionDuration: null,
        price: totalPrice,
      },
    }));
    return { success: true };
  };

  const buildOnBlock = (
    blockId: string,
    buildingType: BuildingType,
    userId: string,
    extraData?: Partial<CompanyData> | Partial<PowerPlantData>
  ): { success: boolean; buildingId?: string } => {
    const block = blocks[blockId];
    if (!block || block.ownerId !== userId) return { success: false };

    const lv = getLandValueByLatLng(block.lat, block.lng);
    const buildingId = `${buildingType}-${Date.now()}`;
    const newBuilding: Building = { type: buildingType, builtAt: new Date().toISOString(), buildingId };
    const updatedBuildings = [...block.buildings, newBuilding];

    // Supabaseに建物情報を更新
    supabase
      .from("lands")
      .update({ buildings: updatedBuildings, updated_at: new Date().toISOString() })
      .eq("id", blockId)
      .eq("owner_id", userId);

    setBlocks((prev) => ({
      ...prev,
      [blockId]: {
        ...prev[blockId],
        buildings: updatedBuildings,
      },
    }));

    if (buildingType === "company") {
      const companyExtra = extraData as Partial<CompanyData> | undefined;
      const industry: CompanyIndustry = companyExtra?.industry || "technology";
      const spec = INDUSTRY_SPECS[industry];
      // 地価係数で収益・コストをスケール
      const scaledRevenue = Math.round(spec.baseRevenue * lv.landMultiplier);
      const scaledExpenses = Math.round(spec.baseExpenses * lv.landMultiplier);
      const scaledCost = lv.companyCost;
      const newCompany: CompanyData = {
        id: buildingId,
        blockId,
        ownerId: userId,
        name: companyExtra?.name || "新しい会社",
        industry,
        description: companyExtra?.description || spec.label + "企業",
        foundedAt: new Date().toISOString(),
        employees: [],
        maxEmployees: spec.maxEmployees,
        products: [],
        finances: {
          revenue: 0,
          expenses: 0,
          profit: 0,
          totalAssets: scaledCost,
          totalLiabilities: 0,
          cashBalance: scaledCost,
          monthlyHistory: [],
        },
        stockPrice: Math.round(100 * lv.landMultiplier),
        totalShares: 100000,
        publicShares: 0,
        isPublic: false,
        level: 1,
        reputation: 50,
      };
      setCompanies((prev) => ({ ...prev, [buildingId]: newCompany }));
    }

    if (buildingType === "powerPlant") {
      const plantExtra = extraData as Partial<PowerPlantData> | undefined;
      const plantType: PowerPlantType = plantExtra?.type || "solar";
      const spec = POWER_PLANT_SPECS[plantType];
      // 地価係数でメンテコストをスケール
      const scaledMaintenance = Math.round(spec.maintenanceCost * lv.landMultiplier);
      const newPlant: PowerPlantData = {
        id: buildingId,
        blockId,
        ownerId: userId,
        name: plantExtra?.name || spec.label + "発電所",
        type: plantType,
        capacity: spec.capacity,
        currentOutput: Math.floor(spec.capacity * spec.efficiency / 100),
        efficiency: spec.efficiency,
        fuelLevel: 100,
        builtAt: new Date().toISOString(),
        lastMaintenance: new Date().toISOString(),
        maintenanceCost: scaledMaintenance,
        powerContracts: [],
        totalGenerated: 0,
        co2Emission: spec.co2,
        operatingStatus: "running",
      };
      setPowerPlants((prev) => ({ ...prev, [buildingId]: newPlant }));
    }

    if (buildingType === "house") {
      const newHouse: HouseData = {
        id: buildingId,
        blockId,
        ownerId: userId,
        residents: [],
        maxResidents: 4,
        builtAt: new Date().toISOString(),
        condition: 100,
      };
      setHouses((prev) => ({ ...prev, [buildingId]: newHouse }));
    }

    return { success: true, buildingId };
  };

  const listForAuction = (blockId: string, duration: "8h" | "24h" | "3d", userId: string): boolean => {
    const block = blocks[blockId];
    if (!block || block.ownerId !== userId) return false;
    const durationMs = { "8h": 8 * 3600000, "24h": 24 * 3600000, "3d": 3 * 24 * 3600000 };
    const endTime = new Date(Date.now() + durationMs[duration]).toISOString();

    // Supabaseに更新
    supabase
      .from("lands")
      .update({ is_for_sale: true, auction_end_time: endTime, auction_duration: duration, updated_at: new Date().toISOString() })
      .eq("id", blockId)
      .eq("owner_id", userId);

    setBlocks((prev) => ({
      ...prev,
      [blockId]: { ...prev[blockId], isForSale: true, auctionEndTime: endTime, auctionDuration: duration },
    }));
    const newAuction: AuctionItem = {
      id: `auction-${Date.now()}`,
      blockId,
      blockLat: block.lat,
      blockLng: block.lng,
      sellerId: userId,
      sellerName: block.ownerName || "Unknown",
      startPrice: 100,
      currentBid: 100,
      currentBidder: null,
      endTime,
      duration,
    };
    setAuctions((prev) => [...prev, newAuction]);
    return true;
  };

  const placeBid = (auctionId: string, bidAmount: number, bidderId: string, bidderName: string): boolean => {
    setAuctions((prev) =>
      prev.map((a) =>
        a.id === auctionId && bidAmount > a.currentBid
          ? { ...a, currentBid: bidAmount, currentBidder: bidderName }
          : a
      )
    );
    return true;
  };

  // ─── 会社操作 ─────────────────────────────────────────────
  const hireEmployee = useCallback((companyId: string, employee: Omit<Employee, "id" | "hiredAt">): boolean => {
    const company = companies[companyId];
    if (!company) return false;
    if (company.employees.length >= company.maxEmployees) return false;
    const newEmployee: Employee = {
      ...employee,
      id: `emp-${Date.now()}`,
      hiredAt: new Date().toISOString(),
    };
    setCompanies((prev) => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        employees: [...prev[companyId].employees, newEmployee],
        finances: {
          ...prev[companyId].finances,
          expenses: prev[companyId].finances.expenses + employee.salary,
        },
      },
    }));
    return true;
  }, [companies]);

  const fireEmployee = useCallback((companyId: string, employeeId: string): boolean => {
    const company = companies[companyId];
    if (!company) return false;
    const emp = company.employees.find((e) => e.id === employeeId);
    if (!emp) return false;
    setCompanies((prev) => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        employees: prev[companyId].employees.filter((e) => e.id !== employeeId),
        finances: {
          ...prev[companyId].finances,
          expenses: Math.max(0, prev[companyId].finances.expenses - emp.salary),
        },
      },
    }));
    return true;
  }, [companies]);

  const addProduct = useCallback((companyId: string, product: Omit<Product, "id" | "launchedAt" | "stockCount">): boolean => {
    const company = companies[companyId];
    if (!company) return false;
    const newProduct: Product = {
      ...product,
      id: `prod-${Date.now()}`,
      launchedAt: new Date().toISOString(),
      stockCount: product.monthlyProduction,
    };
    setCompanies((prev) => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        products: [...prev[companyId].products, newProduct],
      },
    }));
    return true;
  }, [companies]);

  const updateProduct = useCallback((companyId: string, productId: string, updates: Partial<Product>) => {
    setCompanies((prev) => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        products: prev[companyId].products.map((p) =>
          p.id === productId ? { ...p, ...updates } : p
        ),
      },
    }));
  }, []);

  const updateCompany = useCallback((companyId: string, updates: Partial<CompanyData>) => {
    setCompanies((prev) => ({
      ...prev,
      [companyId]: { ...prev[companyId], ...updates },
    }));
  }, []);

  // ─── 発電所操作 ───────────────────────────────────────────
  const addPowerContract = useCallback((plantId: string, contract: Omit<PowerContract, "id">): boolean => {
    const plant = powerPlants[plantId];
    if (!plant) return false;
    const totalContracted = plant.powerContracts
      .filter((c) => c.status === "active")
      .reduce((sum, c) => sum + c.contractedMW, 0);
    if (totalContracted + contract.contractedMW > plant.capacity) return false;
    const newContract: PowerContract = { ...contract, id: `contract-${Date.now()}` };
    setPowerPlants((prev) => ({
      ...prev,
      [plantId]: {
        ...prev[plantId],
        powerContracts: [...prev[plantId].powerContracts, newContract],
      },
    }));
    return true;
  }, [powerPlants]);

  const updatePowerPlant = useCallback((plantId: string, updates: Partial<PowerPlantData>) => {
    setPowerPlants((prev) => ({
      ...prev,
      [plantId]: { ...prev[plantId], ...updates },
    }));
  }, []);

  const performMaintenance = useCallback((plantId: string, spendCoins: (n: number) => boolean): boolean => {
    const plant = powerPlants[plantId];
    if (!plant) return false;
    const paid = spendCoins(plant.maintenanceCost);
    if (!paid) return false;
    setPowerPlants((prev) => ({
      ...prev,
      [plantId]: {
        ...prev[plantId],
        lastMaintenance: new Date().toISOString(),
        efficiency: Math.min(100, prev[plantId].efficiency + 20),
        fuelLevel: 100,
        operatingStatus: "running",
      },
    }));
    return true;
  }, [powerPlants]);

  // ─── 株式市場操作 ─────────────────────────────────────────
  const goPublic = useCallback((
    companyId: string,
    publicShares: number,
    ipoPrice: number,
    spendCoins: (n: number) => boolean
  ): boolean => {
    const company = companies[companyId];
    if (!company) return false;
    // IPO手数料: 株価 × 公開株数 × 1%
    const ipoCost = Math.floor(ipoPrice * publicShares * 0.01);
    const paid = spendCoins(ipoCost);
    if (!paid) return false;

    setCompanies((prev) => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        isPublic: true,
        publicShares,
        stockPrice: ipoPrice,
      },
    }));

    // 株式市場データ初期化
    if (!stockMarket[companyId]) {
      const newMarket: StockMarketData = {
        companyId,
        priceHistory: generatePriceHistory(ipoPrice, 1),
        orders: [],
        trades: [],
        shareholders: {
          [company.ownerId]: {
            userId: company.ownerId,
            userName: "オーナー",
            shares: company.totalShares - publicShares,
          },
        },
        dayHigh: ipoPrice,
        dayLow: ipoPrice,
        dayVolume: 0,
        prevClose: ipoPrice,
      };
      setStockMarket((prev) => ({ ...prev, [companyId]: newMarket }));
    }
    return true;
  }, [companies, stockMarket]);

  const placeStockOrder = useCallback((
    order: Omit<StockOrder, "id" | "placedAt" | "status">,
    spendCoins: (n: number) => boolean,
    addCoins: (n: number) => void,
    holdings: Record<string, StockHolding>
  ): { success: boolean; message: string } => {
    const company = companies[order.companyId];
    if (!company) return { success: false, message: "会社が見つかりません" };
    if (!company.isPublic) return { success: false, message: "この会社は未上場です" };

    const market = stockMarket[order.companyId];
    if (!market) return { success: false, message: "市場データがありません" };

    const execPrice = order.orderType === "market"
      ? company.stockPrice
      : (order.limitPrice ?? company.stockPrice);

    if (order.type === "buy") {
      const totalCost = execPrice * order.shares;
      const paid = spendCoins(totalCost);
      if (!paid) return { success: false, message: `コインが不足しています（必要: ${totalCost.toLocaleString()} coins）` };

      // 株主リスト更新
      setStockMarket((prev) => {
        const m = prev[order.companyId];
        const existing = m.shareholders[order.placedBy];
        const newTrade: StockTrade = {
          id: `trade-${Date.now()}`,
          companyId: order.companyId,
          buyerId: order.placedBy,
          sellerId: "market",
          shares: order.shares,
          price: execPrice,
          tradedAt: new Date().toISOString(),
        };
        return {
          ...prev,
          [order.companyId]: {
            ...m,
            shareholders: {
              ...m.shareholders,
              [order.placedBy]: {
                userId: order.placedBy,
                userName: order.placedByName,
                shares: (existing?.shares ?? 0) + order.shares,
              },
            },
            trades: [newTrade, ...m.trades],
            dayVolume: m.dayVolume + order.shares,
            dayHigh: Math.max(m.dayHigh, execPrice),
            dayLow: Math.min(m.dayLow, execPrice),
          },
        };
      });

      // 株価更新（需要で上昇）
      const priceChange = execPrice * 0.001 * Math.sqrt(order.shares);
      setCompanies((prev) => ({
        ...prev,
        [order.companyId]: {
          ...prev[order.companyId],
          stockPrice: Math.round((prev[order.companyId].stockPrice + priceChange) * 100) / 100,
        },
      }));

      return { success: true, message: `${order.shares.toLocaleString()}株を ${execPrice.toLocaleString()} coins/株で購入しました` };
    } else {
      // 売り注文
      const holding = holdings[order.companyId];
      if (!holding || holding.shares < order.shares) {
        return { success: false, message: `保有株数が不足しています（保有: ${holding?.shares ?? 0}株）` };
      }

      const totalRevenue = execPrice * order.shares;
      addCoins(totalRevenue);

      // 株主リスト更新
      setStockMarket((prev) => {
        const m = prev[order.companyId];
        const existing = m.shareholders[order.placedBy];
        const newShares = (existing?.shares ?? 0) - order.shares;
        const newShareholders = { ...m.shareholders };
        if (newShares <= 0) {
          delete newShareholders[order.placedBy];
        } else {
          newShareholders[order.placedBy] = {
            userId: order.placedBy,
            userName: order.placedByName,
            shares: newShares,
          };
        }
        const newTrade: StockTrade = {
          id: `trade-${Date.now()}`,
          companyId: order.companyId,
          buyerId: "market",
          sellerId: order.placedBy,
          shares: order.shares,
          price: execPrice,
          tradedAt: new Date().toISOString(),
        };
        return {
          ...prev,
          [order.companyId]: {
            ...m,
            shareholders: newShareholders,
            trades: [newTrade, ...m.trades],
            dayVolume: m.dayVolume + order.shares,
            dayHigh: Math.max(m.dayHigh, execPrice),
            dayLow: Math.min(m.dayLow, execPrice),
          },
        };
      });

      // 株価更新（供給で下落）
      const priceChange = execPrice * 0.001 * Math.sqrt(order.shares);
      setCompanies((prev) => ({
        ...prev,
        [order.companyId]: {
          ...prev[order.companyId],
          stockPrice: Math.max(1, Math.round((prev[order.companyId].stockPrice - priceChange) * 100) / 100),
        },
      }));

      return { success: true, message: `${order.shares.toLocaleString()}株を ${execPrice.toLocaleString()} coins/株で売却しました（+${totalRevenue.toLocaleString()} coins）` };
    }
  }, [companies, stockMarket]);

  const cancelStockOrder = useCallback((companyId: string, orderId: string, addCoins: (n: number) => void): boolean => {
    const market = stockMarket[companyId];
    if (!market) return false;
    const order = market.orders.find((o) => o.id === orderId);
    if (!order || order.status !== "open") return false;

    // 買い注文キャンセルはコイン返金
    if (order.type === "buy" && order.limitPrice) {
      addCoins(order.limitPrice * order.shares);
    }

    setStockMarket((prev) => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        orders: prev[companyId].orders.map((o) =>
          o.id === orderId ? { ...o, status: "cancelled" } : o
        ),
      },
    }));
    return true;
  }, [stockMarket]);

  const updateStockHoldings = useCallback((
    companyId: string,
    userId: string,
    userName: string,
    sharesDelta: number
  ) => {
    setStockMarket((prev) => {
      if (!prev[companyId]) return prev;
      const existing = prev[companyId].shareholders[userId];
      const newShares = (existing?.shares ?? 0) + sharesDelta;
      const newShareholders = { ...prev[companyId].shareholders };
      if (newShares <= 0) {
        delete newShareholders[userId];
      } else {
        newShareholders[userId] = { userId, userName, shares: newShares };
      }
      return {
        ...prev,
        [companyId]: { ...prev[companyId], shareholders: newShareholders },
      };
    });
  }, []);

  return (
    <GameContext.Provider
      value={{
        blocks, auctions, companies, powerPlants, houses, stockMarket,
        landsLoading,
        purchaseBlock, buildOnBlock, listForAuction, placeBid, getBlock,
        hireEmployee, fireEmployee, addProduct, updateProduct, updateCompany,
        addPowerContract, updatePowerPlant, performMaintenance,
        goPublic, placeStockOrder, cancelStockOrder, getStockMarket, updateStockHoldings,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}
